// src/AdminPanel/ProductList.js
import React, { useState, useEffect } from "react";
import { db } from "../firebase/firebase";
import { collection, addDoc, getDocs, deleteDoc, doc, updateDoc, orderBy, query, writeBatch, deleteField, onSnapshot } from "firebase/firestore";
import toast from "../utils/toast";
import { useSale } from "../contexts/SaleContext";
import CloudinaryUpload from "../components/CloudinaryUpload";
import { getProductBehaviorType, PRODUCT_BEHAVIOR, TYPE_OPTIONS, WEIGHT_QUANTITY_SUGGESTIONS } from "../utils/productUtils";

function ProductList() {
  const { sale, discountLabel } = useSale();
  // ── Common fields ──────────────────────────────────────────────────────────
  const [name, setName] = useState("");
  const [formula, setFormula] = useState("");
  const [type, setType] = useState("");
  const [manufacturedBy, setManufacturedBy] = useState("");
  const [picture, setPicture] = useState("");
  const [description, setDescription] = useState("");
  const [usage, setUsage] = useState("");
  const [batchNo, setBatchNo] = useState("");
  // ── Standard / Weight fields ───────────────────────────────────────────────
  const [price, setPrice] = useState("");
  const [retailPrice, setRetailPrice] = useState("");
  const [quantityPerPack, setQuantityPerPack] = useState("");
  // ── Medicine-specific fields ───────────────────────────────────────────────
  const [stripsPerPack, setStripsPerPack] = useState("");
  const [tabletsPerStrip, setTabletsPerStrip] = useState("");
  const [packEnabled, setPackEnabled] = useState(true);
  const [packPrice, setPackPrice] = useState("");
  const [packStock, setPackStock] = useState("");
  const [stripEnabled, setStripEnabled] = useState(false);
  const [stripPrice, setStripPrice] = useState("");
  const [stripStock, setStripStock] = useState("");
  // ── Derived ────────────────────────────────────────────────────────────────
  const behaviorType = getProductBehaviorType(type);
  const isMedicine = behaviorType === PRODUCT_BEHAVIOR.MEDICINE;
  const isWeight = behaviorType === PRODUCT_BEHAVIOR.WEIGHT;
  const medicineValid = (packEnabled ? packPrice !== "" : true) && (stripEnabled ? stripPrice !== "" : true) && (packEnabled || stripEnabled);
  const isFormValid = name.trim() !== "" && type.trim() !== "" && picture.trim() !== "" && (isMedicine ? medicineValid : price !== "");

  const [products, setProducts] = useState([]);
  const [editingId, setEditingId] = useState(null);
  const [deleteConfirmId, setDeleteConfirmId] = useState(null);
  const [search, setSearch] = useState("");
  const [currentPage, setCurrentPage] = useState(1);
  const [pageSize, setPageSize] = useState(5);
  const PAGE_SIZE_OPTIONS = [5, 15, 25, 50, 100, 200, 400, 800, 1200, 1600];

  const nameOptions = [
    "Panadol Tablets 500mg", "Vitamin C 1000mg", "Glutamax Capsules",
    "Arinac Forte 400/60mg", "Ibuprofen 200mg"
  ];
  const manufacturedByOptions = ["Pfizer", "GSK", "Abbott", "Sanofi", "Novartis"];
  const typeOptions = TYPE_OPTIONS;
  const descriptionOptions = [
    "Pain relief tablets", "Vitamin supplement",
    "Joint support capsules", "Anti-inflammatory medication"
  ];
  const usageOptions = [
    "Take 1 tablet daily", "Take 2 capsules after meals",
    "Apply topically twice a day", "Take with water"
  ];

  useEffect(() => {
    removeStockField();
    const q = query(collection(db, "products"), orderBy("createdAt", "desc"));
    const unsubscribe = onSnapshot(q, (snapshot) => {
      const list = snapshot.docs.map((d) => ({ id: d.id, ...d.data() }));
      setProducts(list);
    }, (err) => {
      console.error(err);
      toast.error("Failed to load products");
    });
    return () => unsubscribe();
  }, []);

  const removeStockField = async () => {
    try {
      const snapshot = await getDocs(collection(db, "products"));
      const docsWithStock = snapshot.docs.filter(d => d.data().stock !== undefined);
      if (docsWithStock.length === 0) return;
      const batch = writeBatch(db);
      docsWithStock.forEach(d => batch.update(d.ref, { stock: deleteField() }));
      await batch.commit();
    } catch (err) {
      console.error("Stock cleanup failed:", err);
    }
  };

  const resetForm = () => {
    setName(""); setFormula(""); setType(""); setManufacturedBy("");
    setPicture("");
    setDescription(""); setUsage(""); setBatchNo("");
    setPrice(""); setRetailPrice(""); setQuantityPerPack("");
    setStripsPerPack(""); setTabletsPerStrip("");
    setPackEnabled(true); setPackPrice(""); setPackStock("");
    setStripEnabled(false); setStripPrice(""); setStripStock("");
    setEditingId(null);
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (!isFormValid) { toast.error("Please fill all required fields."); return; }
    try {
      const finalDiscounts = sale.isActive ? discountLabel || "" : "";
      const base = { name, formula, type, manufacturedBy, picture, discounts: finalDiscounts, discountMode: "auto", description, usage, batchNo };
      let extra = {};
      if (isMedicine) {
        const pPrc = packEnabled && packPrice ? parseFloat(packPrice) : 0;
        const sPrc = stripEnabled && stripPrice ? parseFloat(stripPrice) : 0;
        extra = {
          quantityUnit: "medicine",
          packaging: { stripsPerPack: stripsPerPack ? parseInt(stripsPerPack, 10) : 0, tabletsPerStrip: tabletsPerStrip ? parseInt(tabletsPerStrip, 10) : 0 },
          sellingOptions: {
            pack: { enabled: packEnabled, label: "Full Pack", price: pPrc, stock: packStock ? parseInt(packStock, 10) : 0 },
            strip: { enabled: stripEnabled, label: "Strip", price: sPrc, stock: stripStock ? parseInt(stripStock, 10) : 0 },
          },
          price: packEnabled && pPrc ? pPrc : sPrc,
          retailPrice: packEnabled && pPrc ? pPrc : sPrc,
        };
      } else if (isWeight) {
        extra = { price: parseFloat(price) || 0, retailPrice: parseFloat(retailPrice) || 0, quantityPerPack: quantityPerPack || "", quantityUnit: "weight" };
      } else {
        extra = { price: parseFloat(price) || 0, retailPrice: parseFloat(retailPrice) || 0, quantityPerPack: quantityPerPack ? parseInt(quantityPerPack, 10) : 0, quantityUnit: "count" };
      }
      const productData = { ...base, ...extra };
      if (editingId) {
        await updateDoc(doc(db, "products", editingId), productData);
        toast.success("Product updated");
      } else {
        await addDoc(collection(db, "products"), { ...productData, createdAt: new Date() });
        toast.success("Product uploaded successfully");
      }
      resetForm();
    } catch (error) {
      console.error(error);
      toast.error("Failed to save product");
    }
  };

  const startEdit = (product) => {
    setEditingId(product.id);
    setName(product.name || ""); setFormula(product.formula || ""); setType(product.type || "");
    setManufacturedBy(product.manufacturedBy || ""); setPicture(product.picture || "");
    setDescription(product.description || ""); setUsage(product.usage || "");
    setBatchNo(product.batchNo || "");
    // Standard/weight fields
    setPrice(product.price || ""); setRetailPrice(product.retailPrice || ""); setQuantityPerPack(product.quantityPerPack || "");
    // Medicine fields
    const pkg = product.packaging || {};
    setStripsPerPack(pkg.stripsPerPack || ""); setTabletsPerStrip(pkg.tabletsPerStrip || "");
    const opts = product.sellingOptions || {};
    setPackEnabled(opts.pack?.enabled ?? true); setPackPrice(opts.pack?.price || ""); setPackStock(opts.pack?.stock || "");
    setStripEnabled(opts.strip?.enabled ?? false); setStripPrice(opts.strip?.price || ""); setStripStock(opts.strip?.stock || "");
  };

  const handleDelete = async (id) => {
    try {
      await deleteDoc(doc(db, "products", id));
      toast.success("Product deleted");
      setDeleteConfirmId(null);
      setProducts((prev) => prev.filter((p) => p.id !== id));
    } catch (err) {
      console.error(err);
      toast.error("Failed to delete product");
    }
  };

  // ── Search filter ─────────────────────────────────────────────
  const filtered = search.trim()
    ? products.filter((p) =>
      p.name?.toLowerCase().includes(search.toLowerCase()) ||
      p.formula?.toLowerCase().includes(search.toLowerCase()) ||
      p.type?.toLowerCase().includes(search.toLowerCase()) ||
      p.batchNo?.toLowerCase().includes(search.toLowerCase())
    )
    : products;

  // reset to page 1 whenever search or pageSize changes
  useEffect(() => { setCurrentPage(1); }, [search, pageSize]);

  // ── Pagination ───────────────────────────────────────────────
  const totalPages = Math.max(1, Math.ceil(filtered.length / pageSize));
  const paginated = filtered.slice((currentPage - 1) * pageSize, currentPage * pageSize);

  return (
    <div className="upload-page">

      {/* ── Edit Modal ── */}
      {editingId && (
        <div className="confirm-overlay" onClick={(e) => { if (e.target === e.currentTarget) resetForm(); }}>
          <div className="upload-container edit-mode" style={{ maxHeight: "90vh", overflowY: "auto" }}>

            <div className="form-edit-banner">
              <span>
                <i className="fa-solid fa-pen-to-square"></i> Editing Product
              </span>
              <button className="cancel-edit-btn" onClick={resetForm}>
                <i className="fa-solid fa-xmark"></i> Cancel
              </button>
            </div>

            <h2>Update Product</h2>

            <form className="upload-form" onSubmit={handleSubmit}>

              <div className="form-group">
                <label htmlFor="pl-name">Product Name *</label>
                <input id="pl-name" type="text" placeholder="Product name" value={name}
                  onChange={(e) => setName(e.target.value)} list="pl-nameOpts" />
                <datalist id="pl-nameOpts">{nameOptions.map((n, i) => <option key={i} value={n} />)}</datalist>
              </div>
              <div className="form-group">
                <label htmlFor="pl-formula">Formula</label>
                <input id="pl-formula" type="text" placeholder="e.g. Paracetamol 500mg" value={formula} onChange={(e) => setFormula(e.target.value)} />
              </div>
              <div className="form-group">
                <label htmlFor="pl-type">Category *</label>
                <input id="pl-type" type="text" placeholder="e.g. Tablet, Syrup, Capsule" value={type} onChange={(e) => setType(e.target.value)} list="pl-typeOpts" />
                <datalist id="pl-typeOpts">{typeOptions.map((t, i) => <option key={i} value={t} />)}</datalist>
              </div>

              {/* MEDICINE — Packaging + Selling Options */}
              {isMedicine && (<>
                <div className="form-section-header"><i className="fa-solid fa-box-open" /> Packaging Info</div>
                <div style={{ display: "grid", gridTemplateColumns: "1fr 1fr", gap: 12 }}>
                  <div className="form-group">
                    <label>Strips per Pack</label>
                    <input type="number" min="0" value={stripsPerPack} onChange={(e) => setStripsPerPack(e.target.value)} placeholder="e.g. 10" />
                  </div>
                  <div className="form-group">
                    <label>Tablets per Strip</label>
                    <input type="number" min="0" value={tabletsPerStrip} onChange={(e) => setTabletsPerStrip(e.target.value)} placeholder="e.g. 10" />
                  </div>
                </div>
                <div className="form-section-header"><i className="fa-solid fa-tag" /> Selling Options <small style={{ fontWeight: 400, color: "#888" }}>(at least one required)</small></div>
                <div style={{ border: `2px solid ${packEnabled ? "#d2222d" : "#e0e0e0"}`, borderRadius: 8, padding: "12px 14px", marginBottom: 10, background: packEnabled ? "#fff8f8" : "#fafafa" }}>
                  <label style={{ display: "flex", alignItems: "center", gap: 8, fontWeight: 600, cursor: "pointer", marginBottom: packEnabled ? 10 : 0 }}>
                    <input type="checkbox" checked={packEnabled} onChange={(e) => setPackEnabled(e.target.checked)} /> Sell as Full Pack
                  </label>
                  {packEnabled && <div style={{ display: "grid", gridTemplateColumns: "1fr 1fr", gap: 12 }}>
                    <div className="form-group" style={{ margin: 0 }}><label>Pack Price (Rs.) *</label><input type="number" min="0" value={packPrice} onChange={(e) => setPackPrice(e.target.value)} placeholder="e.g. 1200" /></div>
                    <div className="form-group" style={{ margin: 0 }}><label>Pack Stock</label><input type="number" min="0" value={packStock} onChange={(e) => setPackStock(e.target.value)} placeholder="e.g. 20" /></div>
                  </div>}
                </div>
                <div style={{ border: `2px solid ${stripEnabled ? "#d2222d" : "#e0e0e0"}`, borderRadius: 8, padding: "12px 14px", marginBottom: 10, background: stripEnabled ? "#fff8f8" : "#fafafa" }}>
                  <label style={{ display: "flex", alignItems: "center", gap: 8, fontWeight: 600, cursor: "pointer", marginBottom: stripEnabled ? 10 : 0 }}>
                    <input type="checkbox" checked={stripEnabled} onChange={(e) => setStripEnabled(e.target.checked)} /> Sell as Strip
                  </label>
                  {stripEnabled && <div style={{ display: "grid", gridTemplateColumns: "1fr 1fr", gap: 12 }}>
                    <div className="form-group" style={{ margin: 0 }}><label>Strip Price (Rs.) *</label><input type="number" min="0" value={stripPrice} onChange={(e) => setStripPrice(e.target.value)} placeholder="e.g. 140" /></div>
                    <div className="form-group" style={{ margin: 0 }}><label>Strip Stock</label><input type="number" min="0" value={stripStock} onChange={(e) => setStripStock(e.target.value)} placeholder="e.g. 80" /></div>
                  </div>}
                </div>
              </>)}

              {/* NON-MEDICINE — standard price / quantity */}
              {!isMedicine && (<>
                <div className="form-group">
                  <label htmlFor="pl-price">Price (Selling Price) *</label>
                  <input id="pl-price" type="number" placeholder="Selling price" value={price} onChange={(e) => setPrice(e.target.value)} min="0" />
                </div>
                <div className="form-group">
                  <label htmlFor="pl-retail">Retail Price (Original Price)</label>
                  <input id="pl-retail" type="number" placeholder="Original/retail price" value={retailPrice} onChange={(e) => setRetailPrice(e.target.value)} min="0" />
                </div>
                <div className="form-group">
                  <label htmlFor="pl-qty">{isWeight ? "Pack Size / Weight" : "Size"}
                    {isWeight && <span style={{ marginLeft: 8, fontSize: 11, background: "#1565c0", color: "#fff", borderRadius: 4, padding: "2px 7px", fontWeight: 600 }}>Weight / Volume</span>}
                  </label>
                  <input id="pl-qty" type="text" placeholder={isWeight ? "e.g. 200ml, 500ml, 1kg" : "e.g. 10, 20, 100"} value={quantityPerPack} onChange={(e) => setQuantityPerPack(e.target.value)} list="pl-qtyOpts" />
                  <datalist id="pl-qtyOpts">{(WEIGHT_QUANTITY_SUGGESTIONS[type] || []).map((s, i) => <option key={i} value={s} />)}</datalist>
                  {isWeight && <small style={{ color: "#1565c0", marginTop: 4, display: "block" }}>Enter pack size / weight.</small>}
                </div>
              </>)}

              <div className="form-group">
                <label htmlFor="manufacturedBy">Manufactured By</label>
                <input id="manufacturedBy" type="text" placeholder="Manufacturer / brand name" value={manufacturedBy}
                  onChange={(e) => setManufacturedBy(e.target.value)} list="manufacturedByOptions" />
                <datalist id="manufacturedByOptions">
                  {manufacturedByOptions.map((b, i) => <option key={i} value={b} />)}
                </datalist>
              </div>

              <div className="form-group">
                <label htmlFor="pl-batchNo">Batch No.</label>
                <input id="pl-batchNo" type="text" placeholder="Enter Batch Number" value={batchNo}
                  onChange={(e) => setBatchNo(e.target.value)} />
              </div>

              <div className="form-group">
                <CloudinaryUpload
                  id="pl-edit-picture"
                  label="Product Image *"
                  required
                  value={picture}
                  onChange={(url) => setPicture(url)}
                  previewShape="square"
                />
              </div>

              {/* ── Auto Discount ── */}
              <div className="form-group">
                <label htmlFor="discounts">
                  Discount Badge
                  {sale.isActive && (
                    <span style={{ marginLeft: "8px", fontSize: "11px", background: "#d2222d", color: "#fff", borderRadius: "4px", padding: "2px 7px", fontWeight: "600" }}>
                      Auto
                    </span>
                  )}
                </label>
                <input
                  id="discounts"
                  type="text"
                  value={sale.isActive ? discountLabel || "" : ""}
                  readOnly
                  disabled
                  placeholder={sale.isActive ? discountLabel || "No sale active" : "No active sale"}
                  style={{ background: "#f5f5f5", color: "#888", cursor: "not-allowed" }}
                />
                {sale.isActive
                  ? <small style={{ color: "#d2222d", marginTop: "4px", display: "block" }}>Discount is set globally via <strong>Manage Sale</strong>.</small>
                  : <small style={{ color: "#aaa", marginTop: "4px", display: "block" }}>Enable a sale from <strong>Manage Sale</strong> to auto-fill this.</small>
                }
              </div>

              <div className="form-group">
                <label htmlFor="description">Description</label>
                <input id="description" type="text" value={description}
                  onChange={(e) => setDescription(e.target.value)}
                  placeholder="Brief description about the medicine" list="descriptionOptions" />
                <datalist id="descriptionOptions">
                  {descriptionOptions.map((d, i) => <option key={i} value={d} />)}
                </datalist>
              </div>

              <div className="form-group">
                <label htmlFor="usage">Usage Instructions</label>
                <input id="usage" type="text" value={usage}
                  onChange={(e) => setUsage(e.target.value)}
                  placeholder="How to use this medicine" list="usageOptions" />
                <datalist id="usageOptions">
                  {usageOptions.map((u, i) => <option key={i} value={u} />)}
                </datalist>
              </div>

              <div className="form-submit-row model-save-btn-outer-div">
                <button type="submit" className="model-save-btn" disabled={!isFormValid}>
                  {editingId
                    ? <><i className="fa-solid fa-floppy-disk"></i> Save Changes</>
                    : <><i className="fa-solid fa-cloud-arrow-up"></i> Upload Product</>
                  }
                </button>
              </div>

            </form>
          </div>
        </div>
      )}

      {/* ── Product List ── */}
      <div className="products-list-container">
        <h2>
          All Products{" "}
          <span className="products-count">
            {search.trim() ? `${filtered.length} / ${products.length}` : products.length}
          </span>
        </h2>

        {/* ── Search Bar ── */}
        <div className="stock-search-bar">
          <input
            type="text"
            className="stock-search-input"
            placeholder="Search by name, formula or type..."
            value={search}
            onChange={(e) => setSearch(e.target.value)}
          />
          {search && (
            <button className="stock-clear-search" onClick={() => setSearch("")}>✕</button>
          )}
        </div>

        {products.length === 0 ? (
          <p className="no-products">No products uploaded yet.</p>
        ) : filtered.length === 0 ? (
          <p className="no-products">No products match your search.</p>
        ) : (
          <div className="table-scroll-wrapper">
            <table className="products-table">
              <thead>
                <tr>
                  <th>Image</th>
                  <th>Product Name</th>
                  <th>Formula</th>
                  <th>Form</th>
                  <th>Selling Price</th>
                  <th>Retail Price</th>
                  <th>Qty/Pack</th>
                  <th>Manufactured By</th>
                  <th>Batch No.</th>
                  <th>Discounts</th>
                  <th>Description</th>
                  <th>Usage</th>
                  <th>Actions</th>
                </tr>
              </thead>
              <tbody>
                {paginated.map((product) => (
                  <tr key={product.id} className={editingId === product.id ? "row-being-edited" : ""}>
                    <td>
                      <img src={product.picture} alt={product.name}
                        className="product-thumb"
                        onError={(e) => { e.target.src = "https://via.placeholder.com/42"; }} />
                    </td>
                    <td data-label="Product Name" className="product-name-cell">{product.name}</td>
                    <td data-label="Formula" className="truncate-cell">{product.formula || "—"}</td>
                    <td data-label="Form">{product.type || "—"}</td>
                    <td data-label="Selling Price">
                      {(() => {
                        const base = product.retailPrice || product.price;
                        let discounted = null;
                        const isManual = product.discountMode === "manual" || (product.discountMode === undefined && product.discounts && product.discounts !== discountLabel);

                        if (isManual && product.discounts) {
                          const match = product.discounts.match(/(\d+)/);
                          if (match && base) {
                            discounted = Math.round(base * (1 - parseInt(match[1], 10) / 100));
                          }
                        } else if (!isManual && sale.isActive && sale.discountPercent > 0 && base) {
                          discounted = Math.round(base * (1 - sale.discountPercent / 100));
                        }

                        return discounted ? (
                          <span>
                            <span className="price-new">₨{discounted}</span>{" "}
                            <span className="price-old" style={{ fontSize: "11px" }}>₨{base}</span>
                          </span>
                        ) : (
                          <span className="price-new">₨{product.price || "—"}</span>
                        );
                      })()}
                    </td>
                    <td data-label="Retail Price" className="price-old">
                      {product.retailPrice ? `₨${product.retailPrice}` : "—"}
                    </td>
                    <td data-label="Qty/Pack">{product.quantityPerPack || "—"}</td>
                    <td data-label="Manufactured By">{product.manufacturedBy || "—"}</td>
                    <td data-label="Batch No.">{product.batchNo || "—"}</td>
                    <td data-label="Discounts">{product.discounts || "—"}</td>
                    <td data-label="Description" className="truncate-cell">{product.description || "—"}</td>
                    <td data-label="Usage" className="truncate-cell">{product.usage || "—"}</td>
                    <td data-label="Actions">
                      <div className="action-buttons">
                        <button className="edit-btn" onClick={() => startEdit(product)}>
                          <i className="fa-solid fa-pen"></i> Edit
                        </button>
                        <button className="delete-btn" onClick={() => setDeleteConfirmId(product.id)}>
                          <i className="fa-solid fa-trash"></i> Delete
                        </button>
                      </div>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}
      </div>

      {/* ── Pagination ── */}
      {products.length > 0 && (
        <div className="pagination-bar">
          <div className="pagination-info">
            Showing {Math.min((currentPage - 1) * pageSize + 1, products.length)}–{Math.min(currentPage * pageSize, products.length)} of {products.length}
          </div>
          <div className="pagination-controls">
            <button className="page-btn" onClick={() => setCurrentPage(1)} disabled={currentPage === 1}>«</button>
            <button className="page-btn" onClick={() => setCurrentPage((p) => Math.max(1, p - 1))} disabled={currentPage === 1}>‹</button>
            {Array.from({ length: totalPages }, (_, i) => i + 1)
              .filter((p) => p === 1 || p === totalPages || Math.abs(p - currentPage) <= 1)
              .reduce((acc, p, idx, arr) => {
                if (idx > 0 && p - arr[idx - 1] > 1) acc.push("...");
                acc.push(p);
                return acc;
              }, [])
              .map((item, i) =>
                item === "..." ? (
                  <span key={`ellipsis-${i}`} className="page-ellipsis">…</span>
                ) : (
                  <button
                    key={item}
                    className={`page-btn${currentPage === item ? " active" : ""}`}
                    onClick={() => setCurrentPage(item)}
                  >{item}</button>
                )
              )}
            <button className="page-btn" onClick={() => setCurrentPage((p) => Math.min(totalPages, p + 1))} disabled={currentPage === totalPages}>›</button>
            <button className="page-btn" onClick={() => setCurrentPage(totalPages)} disabled={currentPage === totalPages}>»</button>
          </div>
          <div className="pagination-size">
            <label>Rows per page:</label>
            <select value={pageSize} onChange={(e) => { setPageSize(Number(e.target.value)); setCurrentPage(1); }}>
              {PAGE_SIZE_OPTIONS.map((s) => <option key={s} value={s}>{s}</option>)}
            </select>
          </div>
        </div>
      )}

      {/* ── Delete Modal ── */}
      {deleteConfirmId && (
        <div className="confirm-overlay">
          <div className="confirm-box">
            <div className="confirm-icon">
              <i className="fa-solid fa-triangle-exclamation"></i>
            </div>
            <h3>Delete Product?</h3>
            <p>This action cannot be undone.</p>
            <div className="confirm-actions">
              <button className="confirm-delete-btn" onClick={() => handleDelete(deleteConfirmId)}>
                <i className="fa-solid fa-trash"></i> Yes, Delete
              </button>
              <button className="cancel-btn" onClick={() => setDeleteConfirmId(null)}>
                Cancel
              </button>
            </div>
          </div>
        </div>
      )}

    </div>
  );
}

export default ProductList;
